import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Notification from '../../model/Notification'
import { notificationService } from '../../service/domain/NotificationService'
import { useUser } from '../user/useUser'
import { optimisticUpdate } from '../util/optimisticUpdate'

export const NOTIFICATIONS_QUERY_KEY = (userID: string | undefined) => ['notifications', userID]

export function useNotifications() {
  const queryClient = useQueryClient()
  const { data: user } = useUser()

  const { data: notifications = [], isLoading } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY(user?.id),
    queryFn: () => (user ? notificationService.getNotifications(user.id) : []),
    enabled: !!user,
    refetchOnWindowFocus: true,
  })

  const { mutate: markRead, isError: markReadFailed } = useMutation({
    mutationFn: (unread: Notification[]) =>
      Promise.all(unread.map((notification) => notificationService.markAsRead(notification))),
    ...optimisticUpdate<Notification[], Notification[]>(
      queryClient,
      NOTIFICATIONS_QUERY_KEY(user?.id),
      (current, unread) => {
        const ids = new Set(unread.map((notification) => notification.id))
        return current.map((notification) =>
          ids.has(notification.id) ? notification.markAsRead() : notification,
        )
      },
    ),
  })

  const markUnreadAsRead = (candidates: Notification[]) => {
    const unread = candidates.filter((notification) => !notification.read)
    if (unread.length > 0) markRead(unread)
  }

  const inboxNotifications = notifications
    .filter((notification) => notification.type !== 'TEAM_CHAT')
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())

  const chatNotifications = notifications.filter(
    (notification) => !notification.read && notification.type === 'TEAM_CHAT',
  )
  const unreadInboxCount = inboxNotifications.filter((notification) => !notification.read).length

  return {
    notifications: inboxNotifications,
    chatNotifications,
    isLoading,
    markAsRead: (notification: Notification) => markUnreadAsRead([notification]),
    markAllAsRead: () => markUnreadAsRead(inboxNotifications),
    readChatNotifications: () => {
      if (!markReadFailed) markUnreadAsRead(chatNotifications)
    },
    unreadCount: unreadInboxCount,
    unreadChatCount: chatNotifications.length,
  }
}
